import { all, call, put, select, take } from 'redux-saga/effects';
import center from '@turf/center';
import centroid from '@turf/centroid';
import {
  activity_create_function,
  ActivityStatus,
  ActivitySubtype,
  ActivitySubtypeShortLabels,
  ActivityType,
  MAX_AREA,
  populateSpeciesArrays
} from 'sharedAPI';
import { kinks } from '@turf/turf';
import { Feature, FeatureCollection } from 'geojson';

import { PayloadAction } from '@reduxjs/toolkit';
import cloneDeep from 'lodash.clonedeep';
import { Md5 } from 'ts-md5';
import {
  autoFillNameByPAC,
  autoFillTotalBioAgentQuantity,
  autoFillTotalReleaseQuantity
} from 'rjsf/business-rules/populateCalculatedFields';
import { selectActivity } from 'state/reducers/activity';
import { selectAuth } from 'state/reducers/auth';
import { isLinkedTreatmentSubtype, populateJurisdictionArray } from 'utils/addActivity';
import { getFieldsToCopy } from 'rjsf/business-rules/formDataCopyFields';
import { getClosestWells } from 'utils/closestWellsHelpers';
import { calc_utm } from 'utils/utm';
import { calculateGeometryArea, calculateLatLng } from 'utils/geometryHelpers';
import { InvasivesAPI_Call } from 'hooks/useInvasivesApi';
import { selectNetworkConnected, selectNetworkState } from 'state/reducers/network';
import GeoShapes from 'constants/geoShapes';
import geomWithinBC from 'utils/geomWithinBC';
import mappingAlertMessages from 'constants/alerts/mappingAlerts';
import { AlertSeverity, AlertSubjects } from 'constants/alertEnums';
import Alerts from 'state/actions/alerts/Alerts';
import Prompt from 'state/actions/prompts/Prompt';
import UserSettings from 'state/actions/userSettings/UserSettings';
import Activity, { INewActivity } from 'state/actions/activity/Activity';
import UploadedPhoto from 'interfaces/UploadedPhoto';
import { RecordCacheServiceFactory } from 'utils/record-cache/context';
import UserRecord from 'interfaces/UserRecord';
import MapActions from 'state/actions/map';
import { TreatmentIdsRequestOnline } from 'state/actions/activity/Suggestions';
import { selectUserSettings } from 'state/reducers/userSettings';
import { buildTimeConfig } from 'state/configuration/build-time-config';
import DrawToolActions, { IUpdateShapeSuccess } from 'state/actions/drawtool/drawToolActions';

function* handle_ACTIVITY_GET_REQUEST(action: PayloadAction<string>) {
  try {
    if (buildTimeConfig.MOBILE) {
      yield put(Activity.getLocal(action.payload));
    } else {
      yield put(Activity.getNetworkRequest(action.payload));
    }
  } catch (e) {
    console.error(e);
  }
}

function* handle_ACTIVITY_COPY_REQUEST() {
  try {
    const activityState = yield select(selectActivity);
    const activityData = { ...activityState.activity.form_data.activity_data };
    const activitySubtypeData = { ...activityState.activity.form_data.activity_subtype_data };

    // call business rule function to exclude certain fields of the activity_data of the form data
    const activityDataToCopy = getFieldsToCopy(activityData, activitySubtypeData).activityData;

    const formDataToCopy = {
      ...activityState.activity.form_data,
      activity_data: activityDataToCopy
    };
    yield put(Activity.copySuccess(formDataToCopy));
  } catch (e) {
    console.error(e);
  }
}

const checkForMisLabledMultiPolygon = (input) => {
  if (input.geometry.type !== 'MultiPolygon') {
    return false;
  }
  if (input.geometry.coordinates.length === 1 && input.geometry.coordinates[0][0][0].length === 2) {
    return true;
  }
  return false;
};

const fixMisLabledMultiPolygon = (input) => {
  if (checkForMisLabledMultiPolygon(input)) {
    return {
      ...input,
      type: 'Feature',
      geometry: { ...input.geometry, type: 'Polygon', coordinates: [...input.geometry.coordinates[0]] }
    };
  } else {
    return input;
  }
};

export function* handle_ACTIVITY_UPDATE_GEO_REQUEST(action: PayloadAction<Feature[]>) {
  const activityState = yield select(selectActivity);
  try {
    const modifiedPayload = JSON.parse(JSON.stringify(action.payload));
    const { latitude, longitude } = calculateLatLng(modifiedPayload) || {};

    let utm: [number, number, number] | null = null;
    if (latitude && longitude) {
      utm = calc_utm(longitude, latitude);
    }

    if (modifiedPayload.length > 0 && modifiedPayload[0].geometry.type === GeoShapes.Point) {
      if (!modifiedPayload[0].properties.radius) {
        /* When radius is missing from point  payload, Prompt user for input, and refire the geometry in the callback, with radius added. */
        yield put(
          Prompt.number({
            title: 'Area Needed',
            prompt: 'Enter the area of geometry in m\u00b2',
            min: 1,
            max: 10,
            callback: (userEnteredArea: number) => {
              const radiusBasedOnArea = Math.sqrt(userEnteredArea / Math.PI);
              modifiedPayload[0].properties.radius = radiusBasedOnArea;
              return [DrawToolActions.updateGeo(modifiedPayload)];
            }
          })
        );
        return;
      }
    }

    let reported_area = calculateGeometryArea(modifiedPayload.geometry);

    if (modifiedPayload.length === 0) {
      const payload: IUpdateShapeSuccess = {
        geometry: modifiedPayload.geometry,
        lat: latitude,
        long: longitude,
        reported_area: reported_area,
        Well_Information: []
      };
      if (utm) payload.utm = utm;

      yield put(DrawToolActions.updateGeoSuccess(payload));
      return;
    }

    const sanitizedGeo = fixMisLabledMultiPolygon(modifiedPayload[0]) || [];

    const isWIPLinestring = sanitizedGeo.geometry.type === GeoShapes.LineString;
    const isPointGeometry = sanitizedGeo.geometry.type === GeoShapes.Point;

    reported_area = calculateGeometryArea([sanitizedGeo]);

    if (!isPointGeometry) {
      const hasSelfIntersections = kinks(sanitizedGeo.geometry).features.length > 0;
      const hasHoles = sanitizedGeo.geometry.coordinates.length > 1 && sanitizedGeo.geometry.type === GeoShapes.Polygon; // check for intersections after LineStrings are converted to Polygons

      if (hasSelfIntersections || hasHoles) {
        yield put(Activity.updateGeoFailure({ geometry: [{ ...sanitizedGeo, properties: { error: 'true' } }] }));
        yield put(Alerts.create(mappingAlertMessages.containsIntersections));
        return;
      }
      sanitizedGeo.properties = { error: 'false' };
    }

    const wellInformationArr: Array<{
      well_id: string;
      well_proximity: string;
    }> = [];

    let wellsAreWithinRecordArea = false;

    if (reported_area < MAX_AREA && !isWIPLinestring && latitude && longitude) {
      const nearestWells = yield getClosestWells(sanitizedGeo);
      wellsAreWithinRecordArea = nearestWells.areWellsInside;
      if (nearestWells?.well_objects.length === 0) {
        wellInformationArr.push({
          well_id: 'No wells found',
          well_proximity: 'No wells found'
        });
      } else {
        nearestWells.well_objects.forEach((well) => {
          if (well.proximity || well.inside) {
            wellInformationArr.push({
              well_id: well.properties.WELL_TAG_NUMBER.toString(),
              well_proximity: well.inside ? '0' : well.proximity.toString()
            });
          }
        });
      }
    }

    let isWithinBC = false;

    if (sanitizedGeo) {
      try {
        isWithinBC = yield call(geomWithinBC, sanitizedGeo);
      } catch (err) {
        yield put(Alerts.create(mappingAlertMessages.cannotValidateRegion));
        console.error(err);
        return;
      }
    }

    if (
      wellsAreWithinRecordArea &&
      activityState.activity.activity_subtype === ActivitySubtype.Treatment_ChemicalPlant
    ) {
      yield put(Alerts.create(mappingAlertMessages.wellsInsideTreatmentArea));
    }
    if (!isWithinBC && !isWIPLinestring) {
      yield put(Alerts.create(mappingAlertMessages.notWithinBC));
      return;
    }
    const payload: IUpdateShapeSuccess = {
      geometry: [JSON.parse(JSON.stringify(sanitizedGeo))] as Feature[],
      lat: latitude,
      long: longitude,
      reported_area,
      Well_Information: wellInformationArr
    };
    if (utm) {
      payload.utm = utm;
    }
    yield put(DrawToolActions.updateGeoSuccess(payload));
  } catch (e) {
    console.error('ERROR', e);
  }
}

export function* handle_ACTIVITY_SAVE_SUCCESS() {
  const activity_id = yield select((state) => state.ActivityPage.activity.activity_id);
  try {
    yield put(Activity.get(activity_id));
    yield put(
      Alerts.create({
        autoClose: 5,
        content: 'Activity saved successfully',
        severity: AlertSeverity.Success,
        subject: AlertSubjects.Form
      })
    );

    yield put(MapActions.initRequest());
  } catch (e) {
    console.error(e);
  }
}

export function* handle_ACTIVITY_SAVE_REQUEST(action) {
  const activityState = yield select(selectActivity);

  if (buildTimeConfig.MOBILE) {
    yield put(
      Activity.Offline.save({
        id: activityState?.activity?.activity_id,
        data: activityState?.activity
      })
    );
  } else {
    try {
      yield put(
        Activity.saveNetworkRequest({
          activity_id: activityState.activity_id,
          updatedFormData: action.payload?.updatedFormData
        })
      );
    } catch (e) {
      console.error(e);
    }
  }
}

export function* handle_ACTIVITY_CREATE_REQUEST(action: PayloadAction<INewActivity>) {
  try {
    const authState = yield select(selectAuth);

    const username = authState.username;
    const displayName = authState.displayName;

    const newActivity = yield call(
      activity_create_function,
      action.payload.type,
      action.payload.subType,
      username,
      displayName,
      authState.extendedInfo.pac_number,
      buildTimeConfig.PLATFORM
    );

    if (buildTimeConfig.MOBILE) {
      yield put(Activity.createLocal({ id: newActivity.activity_id, data: newActivity }));
    } else {
      yield put(Activity.createNetwork(newActivity));
    }
  } catch (e) {
    console.error(e);
  }
}

export function* handle_ACTIVITY_CREATE_SUCCESS(action: PayloadAction<string>) {
  try {
    const userSettingsState = yield select(selectUserSettings);

    yield put(UserSettings.Activity.setActiveActivityId(action.payload));
    yield put(UserSettings.closeNewRecordDialogue());
    yield put(Activity.get(action.payload));

    // If duplicating a record, wait for the success before pasting data in.
    if (userSettingsState.newRecordDialogueState.viewLayout === 'duplicate') {
      const result = yield take([Activity.getSuccess, Activity.getFailure]);
      if (result.type === Activity.getSuccess.type) {
        yield put(Activity.paste());
      }
    }
  } catch (e) {
    console.error(e);
  }
}

export function* handle_ACTIVITY_ON_FORM_CHANGE_REQUEST(action: PayloadAction<UserRecord>) {
  /**
   * Set Null Fields to Undefined. RJSF Will clear out undefined values, but null values persist and and throw validation
   */
  const setNullsToUndefined = (data: unknown): unknown => {
    if (data === null) {
      return undefined;
    }
    if (Array.isArray(data)) {
      return data.map(setNullsToUndefined);
    }
    if (typeof data === 'object' && data !== undefined) {
      const result: Record<string, unknown> = {};
      for (const key in data) {
        if (Object.hasOwn(data, key)) {
          result[key] = setNullsToUndefined(data[key]);
        }
      }
      return result;
    }
    return data;
  };

  try {
    const previousState = yield select(selectActivity);
    const previousActivityState = previousState.activity;

    try {
      let updatedFormData = setNullsToUndefined(cloneDeep(action.payload));
      if (
        previousActivityState.activity_type === ActivityType.Biocontrol ||
        [
          ActivitySubtype.Treatment_BiologicalPlant,
          ActivitySubtype.Monitoring_BiologicalDispersal,
          ActivitySubtype.Monitoring_BiologicalTerrestrialPlant
        ].includes(previousActivityState.activity_subtype)
      ) {
        // auto-fills total release quantity (only on biocontrol release activity)
        updatedFormData = autoFillTotalReleaseQuantity(updatedFormData);
        // auto-fills total bioagent quantity (only on biocontrol release monitoring activity)
        updatedFormData = autoFillTotalBioAgentQuantity(updatedFormData);
      }

      if (previousState.activity.activity_type === ActivityType.Treatment && previousState.suggestedPersons) {
        updatedFormData = autoFillNameByPAC(updatedFormData, previousState.suggestedPersons);
      }

      let updatedActivity = populateSpeciesArrays({ ...cloneDeep(previousActivityState), form_data: updatedFormData });
      updatedActivity = populateJurisdictionArray(updatedActivity);
      updatedActivity.map_symbol = updatedActivity.species_positive.join(', ');
      yield put(Activity.OnFormChangeRequestSuccess(updatedActivity));
    } catch (error) {
      console.error(error);
      yield put(
        Alerts.create({
          subject: AlertSubjects.Global,
          severity: AlertSeverity.Error,
          content: `An unexpected error occurred while updating form contents. ${error}`
        })
      );
    }

    // try to reduce calls to copy geometry
    const linked_id = action.payload.activity_type_data?.linked_id;
    const oldLinkedId = previousActivityState.form_data.activity_type_data?.linked_id;
    const oldCopyGeometry = previousActivityState.form_data.activity_type_data?.copy_geometry;
    const requestGeometryFromExistingRecord =
      action.payload.activity_type_data?.copy_geometry === 'Yes' &&
      linked_id &&
      (oldLinkedId !== linked_id || oldCopyGeometry !== 'Yes');

    if (requestGeometryFromExistingRecord) {
      const { connected } = yield select(selectNetworkState);
      let linked_geo;
      if (connected) {
        const networkReturn = yield InvasivesAPI_Call('GET', `/api/activity/${linked_id}`);

        if (networkReturn?.ok) {
          linked_geo = networkReturn.data.geometry;
        }
      }
      if (!linked_geo && buildTimeConfig.MOBILE) {
        const service = yield RecordCacheServiceFactory.getPlatformInstance();
        const record = yield service.loadActivity(linked_id);
        linked_geo = record.geometry;
      }
      yield put(DrawToolActions.updateGeo(linked_geo));
      yield take(DrawToolActions.updateGeoSuccess.type);
    } else if (
      previousActivityState.form_data.activity_type_data?.copy_geometry === 'Yes' &&
      action.payload?.activity_type_data?.copy_geometry === 'No'
    ) {
      yield put(DrawToolActions.updateGeo([]));
      yield take(DrawToolActions.updateGeoSuccess.type);
    }

    //call autofill events
  } catch (e) {
    console.error(e);
  }
}

export function* handle_ACTIVITY_SUBMIT_REQUEST() {
  const activityState = yield select(selectActivity);
  if (activityState?.activity?.geometry?.properties?.error == 'true') return;

  if (buildTimeConfig.MOBILE) {
    yield put(
      Activity.Offline.save({
        id: activityState?.activity?.activity_id,
        data: { ...activityState.activity, form_status: ActivityStatus.SUBMITTED }
      })
    );
  } else {
    try {
      yield put(
        Activity.saveNetworkRequest({ activity_id: activityState.activity_id, form_status: ActivityStatus.SUBMITTED })
      );
    } catch (e) {
      console.error(e);
    }
  }
}

export function* handle_ACTIVITY_DELETE_REQUEST() {
  try {
    yield put(Activity.deleteNetwork());
  } catch (e) {
    console.error(e);
    yield put(Activity.deleteFailure());
  }
}

export function* handle_ACTIVITY_UPDATE_GEO_SUCCESS() {
  try {
    const currentState = yield select(selectActivity);
    const currentActivity = currentState.activity;
    let hasSelfIntersections = false;
    if (
      currentActivity?.geometry?.[0]?.geometry &&
      currentActivity?.geometry?.[0]?.geometry?.type !== GeoShapes.Point
    ) {
      hasSelfIntersections = kinks(currentActivity?.geometry?.[0]?.geometry).features.length > 0;
    }
    const wipLinestring = currentActivity?.geometry?.[0]?.geometry?.type === GeoShapes.LineString;
    const reportedAreaLessThanMaxArea =
      currentActivity?.geometry && currentActivity?.form_data?.activity_data?.reported_area < MAX_AREA;
    if (reportedAreaLessThanMaxArea && !wipLinestring && !hasSelfIntersections) {
      yield put(Activity.Suggestions.jurisdictions(currentActivity.geometry));
      if (isLinkedTreatmentSubtype(currentActivity.activity_subtype)) {
        yield put(Activity.Suggestions.treatmentIdsRequest(currentActivity));
      }
    }
  } catch (e) {
    console.error(e);
  }
}

export function* handle_GET_SUGGESTED_JURISDICTIONS_REQUEST(action) {
  const connected = yield select(selectNetworkConnected);

  try {
    if (connected) {
      yield put(Activity.Suggestions.jurisdictionsOnline(action.payload ?? null));
    }
  } catch (e) {
    console.error(e);
  }
}

export function* handle_ACTIVITY_GET_SUGGESTED_PERSONS_REQUEST() {
  const connected = yield select(selectNetworkConnected);

  try {
    if (connected) {
      yield put(Activity.Suggestions.personsOnline());
    }
  } catch (e) {
    console.error(e);
  }
}

export function* handle_ACTIVITY_GET_SUGGESTED_TREATMENT_IDS_REQUEST(action) {
  const payloadActivity = action.payload;
  const [AuthState, networkState] = yield all([select(selectAuth), select(selectNetworkState)]);

  try {
    // filter Treatments and/or Biocontrol
    const linkedActivitySubtypes: ActivitySubtypeShortLabels[] = (() => {
      switch (payloadActivity.activity_subtype) {
        case 'Activity_Monitoring_MechanicalTerrestrialAquaticPlant':
          return [
            ActivitySubtypeShortLabels.Activity_Treatment_MechanicalPlantTerrestrial,
            ActivitySubtypeShortLabels.Activity_Treatment_MechanicalPlantAquatic
          ];
        case 'Activity_Monitoring_ChemicalTerrestrialAquaticPlant':
          return [
            ActivitySubtypeShortLabels.Activity_Treatment_ChemicalPlantTerrestrial,
            ActivitySubtypeShortLabels.Activity_Treatment_ChemicalPlantAquatic
          ];
        case 'Activity_Monitoring_BiocontrolRelease_TerrestrialPlant':
          return [ActivitySubtypeShortLabels.Activity_Biocontrol_Release];
        default:
          return [];
      }
    })();
    const search_feature = payloadActivity.geometry?.[0]
      ? ({
          type: 'FeatureCollection',
          features: payloadActivity.geometry
        } as FeatureCollection)
      : false;

    if (linkedActivitySubtypes.length > 0 && search_feature) {
      const payload = {
        activity_subtype: linkedActivitySubtypes,
        user_roles: AuthState.accessRoles,
        search_feature: search_feature
      };
      if (networkState.connected) {
        yield put(Activity.Suggestions.treatmentIdsRequestOnline(payload));
      } else {
        yield getLinkedTreatmentsFromCachedRecords(payload);
      }
    }
  } catch (e) {
    console.error(e);
  }
}

export function* getLinkedTreatmentsFromCachedRecords(req: TreatmentIdsRequestOnline) {
  const service = yield RecordCacheServiceFactory.getPlatformInstance();
  const overlappingRecords = yield service.getRecordIdsOverlappingFeature(req.search_feature);
  const treatmentRecords: UserRecord[] = (yield service.getPaginatedCachedActivityRecords(overlappingRecords)).filter(
    (record: UserRecord) => req.activity_subtype.includes(record.activity_subtype_full)
  );

  yield put(
    Activity.Suggestions.treatmentIdsSuccess(
      treatmentRecords.map((record, i) => ({
        label: record.short_id,
        title: record.short_id,
        value: record.activity_id,
        'x-code_sort_order': i + 1
      }))
    )
  );
}

export function* handle_PAN_AND_ZOOM_TO_ACTIVITY() {
  const activityState = yield select(selectActivity);

  const geometry = activityState?.activity?.geometry?.[0];
  if (geometry) {
    const isPoint = geometry.geometry?.type === GeoShapes.Point;
    let target;
    if (isPoint) {
      target = geometry.geometry;
    } else {
      const acentroid = centroid(geometry);

      target = acentroid.geometry;
    }
    yield put(
      MapActions.centerMap({
        lat: target.coordinates[1],
        lng: target.coordinates[0],
        zoom: 16
      })
    );
  }
}

// some form autofill on create stuff will likely need to go here
export function* handle_ACTIVITY_GET_SUCCESS(action: PayloadAction<Record<string, any>>) {
  try {
    const activityState = yield select(selectActivity);
    const type = activityState?.activity?.activity_subtype;

    // needs to be latlng expression
    const isGeo = !!action.payload?.geometry?.[0]?.geometry?.coordinates;

    let centerPoint;
    if (isGeo) {
      centerPoint = center(action.payload?.geometry[0]?.geometry);
    }
    if (centerPoint && isGeo) {
      yield put(UserSettings.Map.setCenter(centerPoint.geometry.coordinates));
    }

    const authState = yield select(selectAuth);
    const userName = authState.username;
    const created_by = action.payload.created_by;
    const createdByUser = userName === created_by;

    // Don't fetch suggestions if the record doesn't belong to the user
    if (createdByUser) {
      yield put(Activity.Suggestions.persons());
      yield put(Activity.Suggestions.jurisdictions(activityState.activity.geometry));
      if (isLinkedTreatmentSubtype(type)) {
        yield put(Activity.Suggestions.treatmentIdsRequest(action.payload));
      }
    }

    yield put(Activity.buildFormSchema(createdByUser));
  } catch (e) {
    console.error(e);
  }
}

export function* handle_ACTIVITY_CHEM_TREATMENT_DETAILS_FORM_ON_CHANGE_REQUEST(
  eventFormData: PayloadAction<Record<PropertyKey, any>>
) {
  try {
    yield put(Activity.onFormChangeRequest(eventFormData.payload));
  } catch (e) {
    console.error(e);
  }
}

export function* handle_ACTIVITY_ADD_PHOTO_REQUEST(action) {
  try {
    if (action.payload) {
      yield put(Activity.Photo.addSuccess(action.payload));
    }
  } catch (e) {
    console.error(e);
    yield put(Activity.Photo.addFailure());
  }
}

export function* handle_ACTIVITY_DELETE_PHOTO_REQUEST(action) {
  const beforeState = yield select(selectActivity);
  const beforeActivity = beforeState.activity;
  const incomingHash = Md5.hashStr(action.payload.encoded_file);
  const media = beforeActivity.media.filter((photo: UploadedPhoto) => {
    if (photo.media_key) {
      return photo.media_key !== action.payload.media_key;
    }
    return incomingHash !== Md5.hashStr(photo.encoded_file);
  });

  const media_keys: Array<unknown> = [];
  if (beforeActivity.media_keys) {
    media_keys.push(
      ...beforeActivity.media_keys.filter((key) => {
        if (action.payload.media_key) {
          return key !== action.payload.media_key;
        }
      })
    );
  }

  const delete_keys: string[] = [];
  delete_keys.push(...beforeActivity.media_delete_keys);

  if (action.payload.media_key) {
    delete_keys.push(action.payload.media_key);
  }
  yield put(
    Activity.Photo.deleteSuccess({
      ...beforeActivity,
      media: media ?? [],
      media_keys: media_keys ?? [],
      media_delete_keys: delete_keys
    })
  );
}

export function* handle_ACTIVITY_EDIT_PHOTO_REQUEST(action) {
  try {
    const beforeState = yield select(selectActivity);
    const beforeActivityMedia: UploadedPhoto[] = structuredClone(beforeState.activity.media);
    const incomingHash = Md5.hashStr(action.payload.encoded_file);
    const idx = beforeActivityMedia.findIndex((photo) => Md5.hashStr(photo.encoded_file) === incomingHash);

    if (idx !== -1) {
      beforeActivityMedia[idx] = action.payload;
      yield put(Activity.Photo.editSuccess(beforeActivityMedia));
    }
  } catch (e) {
    console.error(e);
  }
}

export { handle_ACTIVITY_GET_REQUEST, handle_ACTIVITY_COPY_REQUEST };
